/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    IApplicationLog,
    IConnectionLog,
    IEventLog,
} from 'generated-types';

import { Observable } from 'rxjs';
import { tap, withLatestFrom } from 'rxjs/operators';
import { ComponentStore } from '@ngrx/component-store';

import { LogsType, TLog } from '../../logs.types';
import { ILogsStates, LogsStore } from '../../services/logs.store';

export interface ILogListExpandedContentState {
    applicationLog?: IApplicationLog;
    connectionLog?: IConnectionLog;
    eventLog?: IEventLog;
}

/**
 * @description
 *     An abstract class of state and effect management for log list expanded content.
 *     Must be extended by a concrete expanded content store class to work as a component store.
 * @author Zhiqian Liu
 */
export abstract class LogListExpandedContentStore<T extends ILogListExpandedContentState> extends ComponentStore<T> {
    // *************************************** Effects ****************************************

    /**
     * Update state with the newly switched log, depending on the type of log.
     */
    public readonly setLogBasedOnType = this.effect<TLog>(
        log$ => log$.pipe(
            withLatestFrom(this.logsStore.logsType$),
            tap(([log, logsType]: [TLog, LogsType]) => {
                switch (log && logsType) {
                    case LogsType.APPLICATION:
                        this.setApplicationLog(log as IApplicationLog);
                        break;

                    case LogsType.CONNECTION:
                        this.setConnectionLog(log as IConnectionLog);
                        break;

                    case LogsType.EVENT:
                        this.setEventLog(log as IEventLog);
                        break;
                }
            }),
        ),
    );

    // ****************************** private updaters **********************************

    protected readonly setApplicationLog = this.updater((state, applicationLog: IApplicationLog) => ({
        ...state,
        applicationLog,
    }));

    protected readonly setConnectionLog = this.updater((state, connectionLog: IApplicationLog) => ({
        ...state,
        connectionLog,
    }));

    protected readonly setEventLog = this.updater((state, eventLog: IEventLog) => ({
        ...state,
        eventLog,
    }));

    // ********************************** Abstract Selectors *********************************

    /**
     * Select the log loaded and being inspected.
     * Should be overridden by the concrete store.
     * The override should guarantee an empty object is returned if nothing is loaded.
     */
    public abstract log$: Observable<TLog>;

    constructor(
        protected readonly logsStore: LogsStore<ILogsStates>,
        initialState: T,
    ) {
        super(initialState);
    }
}
